import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { composeQueryParams, initialDashboardPageMetadata } from "../../utils";

export const readManyChatMessagesAsync = createAsyncThunk("chat/readMany", async payload => {
  try {
    const response = await axios.get("/api/chat", {
      params: {
        sort: payload.sort,
        contentId: payload.contentId,
        currentPage: payload.currentPage || 1,
        itemsPerPage: payload.itemsPerPage || 25
      }
    });
    return {
      items: response.data.items,
      metadata: {
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        hasMore: response.data.hasMore,
        totalItems: response.data.totalItems
      }
    };
  } catch (error) {
    console.log(error);
  }
});

export const readAdminChatMessagesAsync = createAsyncThunk(
  "chat/readManyAdmin",
  async (payload, { getState }) => {
    try {
      const { metadata } = getState().chat;
      const params = composeQueryParams(payload, metadata);
      const response = await axios.get("/api/admin/chat", { params });
      return {
        items: response.data.items,
        metadata: {
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          hasMore: response.data.hasMore,
          itemsPerPage: response.data.itemsPerPage,
          totalItems: response.data.totalItems
        }
      };
    } catch (error) {
      console.log(error);
    }
  }
);

export const readRepliesAsync = createAsyncThunk("chat/readReplies", async payload => {
  try {
    const response = await axios.get("/api/replies", {
      params: {
        parentCommentId: payload.parentCommentId,
        currentPage: payload.currentPage || 1,
        itemsPerPage: payload.itemsPerPage || 25
      }
    });
    return {
      parentCommentId: payload.parentCommentId,
      replies: response.data.items,
      pagination: {
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        hasMore: response.data.hasMore,
        totalItems: response.data.totalItems
      }
    };
  } catch (error) {
    console.log(error);
  }
});

export const postChatMessageAsync = createAsyncThunk("chat/upsert", async payload => {
  try {
    const response = await axios.post("/api/chat", payload);
    return { chatMessage: response.data };
  } catch (error) {
    console.log(error);
  }
});

export const updateChatMessageAsync = createAsyncThunk("chat/update", async payload => {
  try {
    const response = await axios.put(`/api/chat/${payload.id}`, {
      message: payload.message,
      moderationCategory: payload.moderationCategory
    });
    return { id: payload.id, message: response.data.message };
  } catch (error) {
    console.log(error);
  }
});

export const deleteChatMessageAsync = createAsyncThunk("/chat/delete", async payload => {
  try {
    // delete as true = delete, delete as false = undelete
    await axios.delete(`/api/chat/${payload.id}/${payload.delete}`);
    return { id: payload.id };
  } catch (error) {
    console.log(error);
  }
});

export const banUserAsync = createAsyncThunk("chat/banUser", async userId => {
  try {
    await axios.delete(`/api/user/ban/${userId}`);
    return { userId };
  } catch (error) {
    console.log(error);
  }
});

export const chatSlice = createSlice({
  name: "chat",
  initialState: {
    loading: true,
    error: null,
    chatMessages: [],
    replies: {},
    metadata: initialDashboardPageMetadata,
    isNewMessage: false
  },
  extraReducers: builder => {
    builder
      .addCase(readManyChatMessagesAsync.pending, (state, action) => {
        state.loading = true;
        state.isNewMessage = false;
        // Reset on a fresh first-page fetch so messages from a previously
        // viewed content don't leak into the next one.
        const requestedPage = action.meta?.arg?.currentPage || 1;
        if (requestedPage === 1) {
          state.chatMessages = [];
        }
      })
      .addCase(readManyChatMessagesAsync.fulfilled, (state, action) => {
        state.loading = false;
        const currentPage = action.payload?.metadata?.currentPage || 1;
        if (currentPage === 1) {
          state.chatMessages = action.payload?.items || [];
        } else {
          state.chatMessages = [...state.chatMessages, ...(action.payload?.items || [])];
          // filter out duplicates
          state.chatMessages = state.chatMessages.filter(
            (message, index, self) => index === self.findIndex(t => t.id === message.id)
          );
        }
        state.metadata = {
          ...state.metadata,
          currentPage: action.payload?.metadata?.currentPage || 1,
          itemsPerPage: action.payload?.metadata?.itemsPerPage || 25,
          totalPages: action.payload?.metadata?.totalPages || 0,
          hasMore: action.payload?.metadata?.hasMore || false,
          totalItems: action.payload?.metadata?.totalItems || 0
        };
      })
      .addCase(readAdminChatMessagesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.chatMessages = action.payload?.items || [];
        state.metadata = {
          ...state.metadata,
          currentPage: action.payload?.metadata?.currentPage || 1,
          itemsPerPage: action.payload?.metadata?.itemsPerPage || 25,
          totalPages: action.payload?.metadata?.totalPages || 0,
          hasMore: action.payload?.metadata?.hasMore || false,
          totalItems: action.payload?.metadata?.totalItems || 0
        };
      })
      .addCase(readManyChatMessagesAsync.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(readRepliesAsync.pending, (state, action) => {
        const parentCommentId = action.meta.arg.parentCommentId;
        if (!state.replies[parentCommentId]) {
          state.replies[parentCommentId] = { loading: true, items: [], pagination: {} };
        } else {
          state.replies[parentCommentId].loading = true;
        }
      })
      .addCase(readRepliesAsync.fulfilled, (state, action) => {
        const { parentCommentId, replies, pagination } = action.payload;
        if (!state.replies[parentCommentId]) {
          state.replies[parentCommentId] = { loading: false, items: [], pagination: {} };
        }
        if (pagination.currentPage === 1) {
          state.replies[parentCommentId].items = replies;
        } else {
          state.replies[parentCommentId].items = [
            ...state.replies[parentCommentId].items,
            ...replies
          ];
        }
        state.replies[parentCommentId].pagination = pagination;
        state.replies[parentCommentId].loading = false;
      })
      .addCase(readRepliesAsync.rejected, (state, action) => {
        const parentCommentId = action.meta.arg.parentCommentId;
        if (state.replies[parentCommentId]) {
          state.replies[parentCommentId].loading = false;
        }
      })
      .addCase(postChatMessageAsync.pending, state => {
        state.loading = true;
      })
      .addCase(postChatMessageAsync.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(postChatMessageAsync.rejected, state => {
        state.loading = false;
      })
      .addCase(updateChatMessageAsync.pending, state => {
        state.loading = true;
      })
      .addCase(updateChatMessageAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { id, message } = action.payload;
        state.chatMessages = state.chatMessages.map(msg =>
          msg.id === id ? { ...msg, ...message } : msg
        );
      })
      .addCase(updateChatMessageAsync.rejected, state => {
        state.loading = false;
      })
      .addCase(deleteChatMessageAsync.pending, state => {
        state.loading = true;
      })
      .addCase(deleteChatMessageAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload;
        const wasDeleting = action.meta.arg.delete === true;

        if (wasDeleting) {
          // Delete operation - remove from state
          state.chatMessages = state.chatMessages?.filter(cm => cm.id !== id);
        } else {
          // Restore operation - update deleted property
          state.chatMessages = state.chatMessages?.map(cm =>
            cm.id === id ? { ...cm, deleted: false } : cm
          );
        }
      })
      .addCase(deleteChatMessageAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(banUserAsync.fulfilled, (state, action) => {
        // Remove messages from banned user
        const userId = action.payload.userId;
        state.chatMessages = state.chatMessages.filter(message => message.User.id !== userId);

        // Remove replies from banned user
        Object.keys(state.replies).forEach(parentId => {
          state.replies[parentId].items = state.replies[parentId].items.filter(
            reply => reply.UserId !== userId
          );
        });
      });
  }
});

export default chatSlice.reducer;
